import * as Sentry from "@sentry/browser";

const SENTRY_DSN: string = process.env.NEXT_PUBLIC_SENTRY_DSN || "";
const TRACES_SAMPLE_RATE: number = 0.5;
const CHUNK_ERROR_PATTERN = /ChunkLoadError|Loading chunk .* failed|Failed to load chunk/i;

const CRITICAL_ERROR_PATTERNS: RegExp[] = [
  /payment/i,
  /stripe/i,
  /authentication/i,
  /auth.*fail/i,
  /login.*fail/i,
  /token.*invalid/i,
  /session.*expired/i,
  /form.*submit/i,
  /validation.*fail/i,
  /vidtreo/i,
  /video.*record/i,
  /ziggeo/i,
  /upload.*fail/i,
  /critical/i,
  /fatal/i,
];

const IGNORABLE_CLIENT_ERROR_PATTERNS: RegExp[] = [
  /^No error message$/i,
  /Navigation prevented/i,
  /Failed to read a named property ['"]Element['"] from 'Window'/i,
  /Blocked a frame with origin/i,
];

const FETCH_NETWORK_ERROR_PATTERNS: RegExp[] = [
  /Failed to fetch/i,
  /NetworkError when attempting to fetch resource/i,
  /Load failed/i,
];

const STATUS_CODE_PATTERN: RegExp = /(?:status[:\s]*|HTTP\s*)(\d{3})\b/i;

const DURATION_5S_MS = 5000;
const DURATION_30S_MS = 30000;
const DURATION_60S_MS = 60000;

const SIZE_1KB = 1024;
const SIZE_100KB = 102400;
const SIZE_1MB = 1048576;
const SIZE_10MB = 10485760;

interface ErrorNetworkConnection {
  effectiveType: string;
  downlink: number | null;
  rtt: number | null;
  saveData: boolean;
}

interface ErrorRequestContext {
  browserOnline: "true" | "false" | "unknown";
  endpoint: string;
  method: string;
  realm: string | null;
  url: string;
  requestStartTime?: number;
  requestDurationMs?: number | null;
  bodySize?: number | null;
  networkConnection?: ErrorNetworkConnection | null;
}

interface ErrorWithRequestContext {
  requestContext?: ErrorRequestContext;
}

function getDurationBucket(durationMs: number): string {
  if (durationMs < DURATION_5S_MS) {
    return "<5s";
  }
  if (durationMs < DURATION_30S_MS) {
    return "5-30s";
  }
  if (durationMs < DURATION_60S_MS) {
    return "30-60s";
  }
  return ">60s";
}

function getBodySizeBucket(size: number): string {
  if (size < SIZE_1KB) {
    return "<1KB";
  }
  if (size < SIZE_100KB) {
    return "1-100KB";
  }
  if (size < SIZE_1MB) {
    return "100KB-1MB";
  }
  if (size < SIZE_10MB) {
    return "1-10MB";
  }
  return ">10MB";
}

function getRelease(): string | undefined {
  const commit = process.env.NEXT_PUBLIC_APP_COMMIT;
  const version = process.env.NEXT_PUBLIC_APP_VERSION;

  if (commit) {
    return `ev-candidate@${commit.substring(0, 7)}`;
  }

  if (version) {
    return `ev-candidate@${version}`;
  }

  return undefined;
}

function getClientEnvironment(): string {
  if (process.env.NODE_ENV === "development") {
    return "development";
  }

  const branch = process.env.NEXT_PUBLIC_APP_BRANCH || "";

  if (branch === "main") {
    return "production";
  }

  if (branch === "develop" || branch === "qa") {
    return "develop";
  }

  if (branch) {
    return `preview-${branch}`;
  }

  return "production";
}

function shouldCaptureReplayForError(event: Sentry.ErrorEvent): boolean {
  if (event.tags?.critical === true) {
    return true;
  }

  if (event.exception?.values?.some((e) => e.mechanism?.handled === false)) {
    return true;
  }

  const errorType = event.exception?.values?.[0]?.type || "";
  const errorMessage = event.exception?.values?.[0]?.value || "";
  const combinedText = `${errorType} ${errorMessage}`;

  return CRITICAL_ERROR_PATTERNS.some((pattern) => pattern.test(combinedText));
}

function getOriginalExceptionText(originalException: unknown): string {
  if (typeof originalException === "string") {
    return originalException;
  }

  if (originalException instanceof Error) {
    return `${originalException.name} ${originalException.message}`.trim();
  }

  if (
    originalException &&
    typeof originalException === "object" &&
    "message" in originalException &&
    typeof (originalException as { message: unknown }).message === "string"
  ) {
    return String((originalException as { message: string }).message);
  }

  return "";
}

function getEventText(event: Sentry.ErrorEvent, hint: Sentry.EventHint): string {
  const originalExceptionText = getOriginalExceptionText(hint.originalException);
  const exceptionText =
    event.exception?.values
      ?.map((value) => `${value.type ?? ""} ${value.value ?? ""}`.trim())
      .filter(Boolean)
      .join(" ") ?? "";
  const logText = event.message || event.logentry?.message || "";

  return [originalExceptionText, exceptionText, logText].filter(Boolean).join(" ").trim();
}

function isEmptyClientError(event: Sentry.ErrorEvent, hint: Sentry.EventHint): boolean {
  return getEventText(event, hint).length === 0;
}

function isIgnorableClientError(event: Sentry.ErrorEvent, hint: Sentry.EventHint): boolean {
  const eventText = getEventText(event, hint);

  return IGNORABLE_CLIENT_ERROR_PATTERNS.some((pattern) => pattern.test(eventText));
}

function isFetchNetworkErrorEvent(event: Sentry.ErrorEvent, hint: Sentry.EventHint): boolean {
  const eventText = getEventText(event, hint);

  return FETCH_NETWORK_ERROR_PATTERNS.some((pattern) => pattern.test(eventText));
}

function getRequestContext(originalException: unknown): ErrorRequestContext | null {
  if (!originalException || typeof originalException !== "object") {
    return null;
  }

  const requestContext = (originalException as ErrorWithRequestContext).requestContext;

  if (!requestContext) {
    return null;
  }

  return requestContext;
}

function initializeSentry(): void {
  if (!SENTRY_DSN) {
    return;
  }

  const environment = getClientEnvironment();
  const shouldMaskSensitiveData = environment !== "development";

  Sentry.init({
    dsn: SENTRY_DSN,
    release: getRelease(),
    environment,
    tracesSampleRate: TRACES_SAMPLE_RATE,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: shouldMaskSensitiveData,
        blockAllMedia: shouldMaskSensitiveData,
        maskAllInputs: shouldMaskSensitiveData,
        networkDetailAllowUrls: [/evaluar\.com/, /evaluardev\.com/],
        networkRequestHeaders: ["X-Request-Id", "Realm"],
        networkResponseHeaders: ["X-Request-Id"],
        beforeErrorSampling: (event) => shouldCaptureReplayForError(event),
      }),
    ],
    beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint): Sentry.ErrorEvent | null {
      if (isEmptyClientError(event, hint) || isIgnorableClientError(event, hint)) {
        return null;
      }

      const requestContext = getRequestContext(hint.originalException);
      if (requestContext) {
        event.tags = {
          ...event.tags,
          api_method: requestContext.method,
          api_realm: requestContext.realm || "unknown",
          browser_online: requestContext.browserOnline,
        };

        if (requestContext.networkConnection) {
          event.tags.network_type = requestContext.networkConnection.effectiveType;
          event.tags.network_save_data = String(requestContext.networkConnection.saveData);
        }

        if (requestContext.requestDurationMs != null) {
          event.tags.request_duration_bucket = getDurationBucket(requestContext.requestDurationMs);
        }

        if (requestContext.bodySize != null) {
          event.tags.body_size_bucket = getBodySizeBucket(requestContext.bodySize);
        }

        const networkContext: Record<string, unknown> = {
          browser_online: requestContext.browserOnline,
        };

        if (requestContext.networkConnection) {
          networkContext.effective_type = requestContext.networkConnection.effectiveType;
          networkContext.downlink_mbps = requestContext.networkConnection.downlink;
          networkContext.rtt_ms = requestContext.networkConnection.rtt;
          networkContext.save_data = requestContext.networkConnection.saveData;
        }

        event.contexts = {
          ...event.contexts,
          api_request: {
            endpoint: requestContext.endpoint,
            method: requestContext.method,
            realm: requestContext.realm || "unknown",
            url: requestContext.url,
            request_duration_ms: requestContext.requestDurationMs ?? null,
            body_size_bytes: requestContext.bodySize ?? null,
          },
          network: networkContext,
        };

        event.request = {
          ...event.request,
          method: event.request?.method || requestContext.method,
          url: event.request?.url || requestContext.url,
        };
      }

      const eventText = getEventText(event, hint);
      const isFetchNetworkError = isFetchNetworkErrorEvent(event, hint);

      if (isFetchNetworkError) {
        event.tags = {
          ...event.tags,
          browser_online:
            event.tags?.browser_online ||
            (typeof navigator === "undefined" ? "unknown" : String(navigator.onLine)),
          fetch_network_error: "true",
        };

        if (typeof navigator !== "undefined" && navigator.onLine === false) {
          return null;
        }
      }

      if (CHUNK_ERROR_PATTERN.test(eventText)) {
        event.tags = {
          ...event.tags,
          chunk_load_error: "true",
        };
      }

      const original = hint.originalException;
      if (original && typeof original === "object" && "status" in original) {
        const status = (original as { status: number }).status;
        if (status === 401 || status === 403) {
          return null;
        }
      }

      if (event.exception?.values) {
        const exceptions = event.exception.values;

        if (exceptions.length > 0) {
          const exception = exceptions[0];

          if (exception.value) {
            const statusCode = extractStatusCode(exception.value);

            if (statusCode === 401 || statusCode === 403) {
              return null;
            }
          }
        }
      }

      return event;
    },
  });
}

function extractStatusCode(errorMessage: string): number | null {
  const match = errorMessage.match(STATUS_CODE_PATTERN);

  if (match) {
    return Number.parseInt(match[1], 10);
  }

  return null;
}

export { initializeSentry };
